'use client'

import * as React from 'react'
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar as CalendarIcon } from 'lucide-react'
import type { DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

interface DatePickerWithRangeProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange
  setDate: (date: DateRange | undefined) => void
  align?: 'start' | 'center' | 'end'
}

const PRESETS = [
  {
    label: 'Hoje',
    range: (): DateRange => {
      const today = new Date()
      return { from: today, to: today }
    },
  },
  {
    label: 'Últimos 7 dias',
    range: (): DateRange => ({ from: subDays(new Date(), 6), to: new Date() }),
  },
  {
    label: 'Últimos 30 dias',
    range: (): DateRange => ({ from: subDays(new Date(), 29), to: new Date() }),
  },
  {
    label: 'Este mês',
    range: (): DateRange => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
  },
  {
    label: 'Mês passado',
    range: (): DateRange => {
      const lastMonth = subMonths(new Date(), 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    },
  },
  {
    label: 'Este ano',
    range: (): DateRange => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }),
  },
]

const DateTriggerButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { date?: DateRange }
>(({ date, className, ...props }, ref) => (
  <Button
    ref={ref}
    id="date"
    variant="outline"
    className={cn(
      'w-full sm:w-75 justify-start text-left font-normal',
      !date && 'text-muted-foreground',
      className
    )}
    {...props}
  >
    <CalendarIcon className="mr-2 h-4 w-4" />
    {date?.from ? (
      date.to ? (
        <>
          {format(date.from, "dd 'de' MMM, yyyy", { locale: ptBR })} -{' '}
          {format(date.to, "dd 'de' MMM, yyyy", { locale: ptBR })}
        </>
      ) : (
        format(date.from, "dd 'de' MMM, yyyy", { locale: ptBR })
      )
    ) : (
      <span>Selecione um período</span>
    )}
  </Button>
))

function PresetButtons({
  setDate,
  layout,
}: {
  setDate: (date: DateRange | undefined) => void
  layout: 'row' | 'column'
}) {
  return (
    <div
      className={cn(
        'flex gap-1.5 p-3 border-border',
        layout === 'row'
          ? 'flex-row overflow-x-auto no-scrollbar border-b'
          : 'flex-col border-r min-w-35'
      )}
    >
      {PRESETS.map((preset) => (
        <Button
          key={preset.label}
          variant="ghost"
          size="sm"
          className="justify-start text-left text-sm font-normal h-8 px-2.5 shrink-0"
          onClick={() => setDate(preset.range())}
        >
          {preset.label}
        </Button>
      ))}
    </div>
  )
}

export function DatePickerWithRange({
  className,
  date,
  setDate,
  align = 'start',
}: DatePickerWithRangeProps) {
  const [isMobile, setIsMobile] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    setIsMobile(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const handleSetDate = (range: DateRange | undefined) => {
    setDate(range)
  }

  if (isMobile) {
    return (
      <div className={cn('grid gap-2', className)}>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <DateTriggerButton date={date} />
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="rounded-t-xl max-h-[80dvh] p-0 [&>button:last-child]:hidden"
          >
            <SheetTitle className="sr-only">Selecionar período</SheetTitle>
            {/* Grab handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
            </div>
            {/* Presets */}
            <div className="flex gap-1.5 px-4 pb-3 overflow-x-auto no-scrollbar">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="text-sm font-normal h-8 px-2.5 shrink-0"
                  onClick={() => {
                    handleSetDate(preset.range())
                    setOpen(false)
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            {/* Calendar */}
            <div className="border-t border-border px-2 pb-6">
              <Calendar
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={1}
                showOutsideDays
                locale={ptBR}
                classNames={{
                  root: 'w-full',
                  months: 'w-full flex flex-col relative',
                  month: 'w-full flex flex-col gap-4',
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <DateTriggerButton date={date} />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align}>
          <div className="flex flex-row">
            <PresetButtons setDate={handleSetDate} layout="column" />
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={setDate}
              numberOfMonths={2}
              showOutsideDays={false}
              locale={ptBR}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
